import {
  FiltersV2TermSearch,
  useFiltersState,
  useFiltersV2Actions,
  useFiltersV2Store
} from "PFApp/components/filters_v2";
import { SearchEntity } from "PFApp/components/search_autocomplete/search_autocomplete.types";
import { Alert } from "PFComponents/alert";
import BrainLoading from "PFComponents/brain_loading/brain_loading";
import { useActivity } from "PFCore/hooks/queries/activity";
import { useMatchesInvalidate } from "PFCore/hooks/queries/matches/use_matches_invalidate";
import { fetchMatches } from "PFCore/services/matches";
import { Activity } from "PFTypes";
import { KnownFiltersV2 } from "PFTypes/filters_v2";
import { RecentSearchesPageKey } from "PFTypes/recent_searches";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useReassignMatches } from "../hooks/use_reassign_matches";
import { MatchesActions } from "./matches_actions";
import { MatchesTable } from "./matches_table";
import { NoMatchesPlaceholder } from "./no_matches_placeholder";
import { RowSelection } from "./reassign_content";

type ReassignContentMatchesProps = {
  sourceProfileIds: number[];
  activity: Activity;
  onNamedResourcesClick?: VoidFunction;
} & RowSelection;

export const ReassignContentMatches = ({
  sourceProfileIds,
  activity,
  onTargetProfileChange,
  rowSelectionState,
  onNamedResourcesClick
}: ReassignContentMatchesProps) => {
  const { t } = useTranslation(["bookingModule", "activities", "translation", "filters"]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { matchesResponse, matchesQueryParams, matchesOrder } = useReassignMatches({
    sourceProfileIds,
    activityId: activity.id
  });
  const matchesResponseIsEmpty = !matchesResponse.isFetching && matchesResponse.matchesData.length === 0;

  const { data: parentActivity } = useActivity(activity.parent_activity_id ?? -1, {
    enabled: !!activity.parent_activity_id && matchesResponseIsEmpty
  });
  const { invalidate: invalidateMatches } = useMatchesInvalidate();

  const handleMatchesRefresh = useCallback(() => {
    setIsRefreshing(true);

    fetchMatches(activity.id, {
      refresh: true,
      ...matchesQueryParams
    })
      .then(() => {
        invalidateMatches();
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  }, [activity.id, matchesQueryParams, invalidateMatches]);

  const isCompleted = activity.state === "complete";

  const { resetValues } = useFiltersV2Actions();
  const { areFiltersDefaultOrEmpty } = useFiltersState();

  const termFilter = useFiltersV2Store((state) => state.mainFilters[KnownFiltersV2.Term]);
  const matchesSearchEnabled = !!termFilter && !isCompleted && matchesResponse.matchesData.length > 0;

  return (
    <>
      <FiltersV2TermSearch
        disabled={!matchesSearchEnabled}
        autocompleteConfig={[{ entity: SearchEntity.Profile, useAsTerm: true }]}
        pageKey={RecentSearchesPageKey.ReassignProfile}
      />
      {!areFiltersDefaultOrEmpty && (
        <Alert
          title={t("activities:show.matches.filtersChangedWarning")}
          action={{
            text: t("filters:restoreAll"),
            icon: "history",
            kind: "text",
            onClick: () => resetValues()
          }}
          multiLineTitle
        />
      )}
      {!isCompleted && (
        <MatchesActions
          onRefresh={handleMatchesRefresh}
          isLoading={isRefreshing || matchesResponse.isFetching}
          lastRefreshAt={matchesResponse.lastRefreshAt}
          orderMeta={matchesOrder.orderMeta}
          onOrderChange={matchesOrder.handleChangeOrder}
        />
      )}
      {isRefreshing && (
        <BrainLoading text={t("bookingModule:bookings.reassign.sidePanel.calculationsStarted")} />
      )}
      {matchesResponseIsEmpty && (
        <NoMatchesPlaceholder
          activity={activity}
          parentActivity={parentActivity}
          onNamedResourcesClick={onNamedResourcesClick}
        />
      )}
      {!isRefreshing && !matchesResponseIsEmpty && (
        <MatchesTable
          onTargetProfileChange={onTargetProfileChange}
          rowSelectionState={rowSelectionState}
          matchesResponse={matchesResponse}
        />
      )}
    </>
  );
};
